/*
 * Copyright (c) 2017 Payoneer Germany GmbH. All rights reserved.
 */

/*
 * OP Config service
 * there are default config files and config provided in installment
 * and config provided by query string,
 * this service set the values used for all the config
 */
const _ = require('underscore');
const escapeHtml = require('escape-html');
const querystring = require('querystring');

const getLocation = () => {
    if (window && window.location instanceof Location) return window.location;
    return {};
};

const DEFAULT_CONFIG = Object.freeze({
    payButton: 'submitBtn',
    payButtonContainer: 'submitBtnContainer',
    liveValidation: true,
    validationDelay: 1000,
    smartSwitch: true,
    suppressIFrameDefault: true,
    useAnimations: true,
    animationSpeed: 200,
    iFrameScaleFunction: function () {
        return screen.width <= 736 ? 1.28 : 1.6;
    },
    summaryPage: false, // a flag that this script is initialized for summary page
    widgetCssUrl: getBaseUrl(getLocation()) + 'widget.min.css',
    cardView: false,
    fullPageLoading: true,
    templateName: 'pp3', //default template
    listDetails: null,
    networkTranslation: {},
    combineRegistrationOptions: false,
    providerRedirectInNewWindow: false, // Handle 3rd party redirects in new tab/window
    providerRedirectWindowSpecs: '', // Optionally pass specs param for window.open
});

const ALLOWED_QUERY_PARAMETER_TYPES = Object.freeze({
    sai: 'string', // legacy support, it will be removed in next iteration
    lang: 'string',
    listId: 'string',
    baseUrl: 'string', // it will be removed in next iteration
    listUrl: 'string', // it will be removed in next iteration
    templateName: 'string',
    widgetCssUrl: 'string',
    animationSpeed: 'number',
    validationDelay: 'number',
    cardView: 'boolean',
    smartSwitch: 'boolean',
    useAnimations: 'boolean',
    liveValidation: 'boolean',
    fullPageLoading: 'boolean',
    suppressIFrameDefault: 'boolean',
    combineRegistrationOptions: 'boolean',
});

const ALLOWED_DOMAINS = [
    'api.live.oscato.com', // Live
    'api.sandbox.oscato.com', // Sandbox
    'api.stage.oscato.com', // Stage
    'api.integration.oscato.com', // Main integration
];

const ALLOWED_QUERY_PARAMETERS = Object.keys(ALLOWED_QUERY_PARAMETER_TYPES);

const ALLOWED_PROTOCOLS = ['http:', 'https:'];

const SUPPORTED_PARAM_TYPES = ['string', 'number', 'boolean'];

function isValidLocation(loc) {
    return Boolean(
        loc &&
            typeof loc === 'object' &&
            Object.keys(loc).length &&
            Object.hasOwnProperty.call(loc, 'href') &&
            Object.hasOwnProperty.call(loc, 'search') &&
            Object.hasOwnProperty.call(loc, 'protocol') &&
            Object.hasOwnProperty.call(loc, 'hostname') &&
            typeof loc.href === 'string' &&
            typeof loc.search === 'string' &&
            typeof loc.protocol === 'string' &&
            typeof loc.hostname === 'string'
    );
}

function getBaseUrl(location) {
    if (!isValidLocation(location)) return '/';

    const url = location.href.split('?')[0];
    return url.substring(0, url.lastIndexOf('/') + 1);
}

function getQueryParamsObj(location) {
    if (!isValidLocation(location)) return {};

    let params = location.search;
    if (params.startsWith('?')) params = params.substring(1); // removes initial '?'

    params = querystring.parse(params);
    return parseStringifiedBooleans(params);
}

function parseStringifiedBooleans(input) {
    return _.mapObject(input, function (val) {
        let temp = false;
        if (val === 'false') temp = false;
        else if (val === 'true') temp = true;
        else temp = val;

        return temp;
    });
}

function getExtendDefaultConfig(inputConfig, queryParamsObject) {
    const queryParams = filterQueryParams(queryParamsObject);
    const extendedConfig = _.extend({}, DEFAULT_CONFIG, inputConfig, queryParams);

    if (extendedConfig.cardView && !inputConfig.widgetCssUrl && !queryParams.widgetCssUrl)
        extendedConfig.widgetCssUrl = getBaseUrl(getLocation()) + 'widget-card-view.min.css';

    if (extendedConfig.cardView && !inputConfig.iFrameScaleFunction && !queryParams.iFrameScaleFunction)
        extendedConfig.iFrameScaleFunction = function () {
            return 1.425;
        };

    return extendedConfig;
}

function applySpecialRules(config) {
    var clonedConfig = _.extend({}, config);

    if (!clonedConfig['listId'] && clonedConfig['sai']) {
        clonedConfig['listId'] = clonedConfig['sai']; // support legacy configuration
    }
    if (clonedConfig['listId'] && !clonedConfig['baseUrl']) {
        clonedConfig['baseUrl'] = getBaseUrl(getLocation());
    }
    if (!clonedConfig['listUrl']) {
        if (clonedConfig['listId']) {
            clonedConfig['listUrl'] = clonedConfig['baseUrl'] + clonedConfig['listId'];
        } else if (clonedConfig['longId']) {
            clonedConfig['listUrl'] = clonedConfig['baseUrl'] + clonedConfig['longId'];
        }
    }
    if (clonedConfig['listUrl'] && !isValidListURL(clonedConfig['listUrl'])) {
        clonedConfig['listUrl'] = '';
    }
    if (clonedConfig['baseUrl'] && !isValidListURL(clonedConfig['baseUrl'])) {
        clonedConfig['baseUrl'] = '';
    }

    return clonedConfig;
}

function getConfig(inputConfig) {
    let queryParams = getQueryParamsObj(getLocation());
    queryParams = filterQueryParams(queryParams);

    return applySpecialRules(getExtendDefaultConfig(inputConfig, queryParams));
}

/**
 * Check if the url is a valid listUrl (baseUrl is part of listUrl)
 *
 * @param {string} url complete URL
 * @returns {boolean} is the url is valid
 */
function isValidListURL(url) {
    try {
        const urlObj = new URL(url);
        return (
            ALLOWED_PROTOCOLS.includes(urlObj.protocol) &&
            (ALLOWED_DOMAINS.includes(urlObj.hostname) || /^api\.[a-z0-9\-_]*\.integration\.oscato\.com$/g.test(urlObj.hostname)) // Other Integration Servers
        );
    } catch (error) {
        return false;
    }
}

function isValidQueryParameter(key, value) {
    if (!(key && typeof key === 'string' && SUPPORTED_PARAM_TYPES.includes(typeof value))) return false;
    if (ALLOWED_QUERY_PARAMETERS.includes(key) && typeof value !== ALLOWED_QUERY_PARAMETER_TYPES[key]) return false;
    if ((key === 'listUrl' || key === 'baseUrl') && !isValidListURL(value)) return false;

    return true;
}

function filterQueryParams(queryParams) {
    let allowedParams = {};
    if (!_.isObject(queryParams)) return allowedParams;

    for (const paramKey in queryParams) {
        let paramValue = queryParams[paramKey];

        if (!isValidQueryParameter(paramKey, paramValue)) continue;
        if (typeof paramValue === 'string') paramValue = escapeHtml(paramValue);

        allowedParams[paramKey] = paramValue;
    }

    return allowedParams;
}

module.exports = {
    DEFAULT_CONFIG,
    ALLOWED_QUERY_PARAMETER_TYPES,

    getConfig,
    getBaseUrl,
    isValidListURL,
    isValidLocation,
    applySpecialRules,
    filterQueryParams,
    getQueryParamsObj,
    isValidQueryParameter,
    getExtendDefaultConfig,
    parseStringifiedBooleans,
};
